import { Handle, NodeToolbar, Position } from "@xyflow/react";
import { NodeData } from "../types";
import PlusIcon from "lucide-react/dist/esm/icons/plus";
import TrashIcon from "lucide-react/dist/esm/icons/trash";
import PencilIcon from "lucide-react/dist/esm/icons/pencil";
import EyeIcon from "lucide-react/dist/esm/icons/eye";
import { Button, ButtonProps } from "@quake/shared-ui/components/ui/button";
import { cn } from "@quake/shared-ui/lib/utils";
import { useWorkflowBuilderStore } from "../store/useWorkflowBuilderStore";
import { useState } from "react";

export interface BaseNodeProps {
  id: string;
  data: NodeData;
  icon?: React.ReactNode;
  color?: string;
  className?: string;
  isSourceConnectable?: boolean;
  isTargetConnectable?: boolean;
  removeTargetHandle?: boolean;
  onAddAction?: ({ branch }: { branch?: "truthy" | "falsy" }) => void;
  onDelete?: () => void;
  onEdit?: () => void;
  showAddAction?: boolean;
  children?: React.ReactNode;
  readOnly?: boolean;
  allowActionPreview?: boolean;
}

export function BaseNode({
  id,
  onAddAction,
  onDelete,
  onEdit,
  data,
  icon,
  color = "bg-blue-500 dark:bg-slate-800",
  className,
  isSourceConnectable = true,
  isTargetConnectable = true,
  removeTargetHandle = false,
  showAddAction = true,
  children,
  readOnly = false,
  allowActionPreview = true,
}: BaseNodeProps) {
  const direction = useWorkflowBuilderStore((state) => state.meta?.direction);
  const [isHovered, setIsHovered] = useState(false);
  const [isHoveredTimeout, setIsHoveredTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const showToolbar = () => {
    if (isHoveredTimeout) {
      clearTimeout(isHoveredTimeout);
    }
    setIsHovered(true);
  };
  const hideToolbar = () => {
    if (isHoveredTimeout) {
      clearTimeout(isHoveredTimeout);
    }
    const timeout = setTimeout(() => {
      setIsHovered(false);
    }, 2000);
    setIsHoveredTimeout(timeout);
  };

  return (
    <div data-node-id={id} onMouseEnter={() => showToolbar()} onMouseLeave={() => hideToolbar()}>
      {allowActionPreview && (
        <NodeToolbar
          isVisible={isHovered}
          position={Position.Top}
          className="flex gap-2"
          onMouseEnter={() => showToolbar()}
          onMouseLeave={() => hideToolbar()}
        >
          <NodeButton variant="ghost" size="icon" onClick={onEdit}>
            {readOnly ? (
              <EyeIcon className="h-4 w-4 text-gray-600 dark:text-slate-200" />
            ) : (
              <PencilIcon className="h-4 w-4 text-gray-600 dark:text-slate-200" />
            )}
          </NodeButton>
          {!readOnly && (
            <NodeButton variant="ghost" size="icon" onClick={onDelete}>
              <TrashIcon className="h-4 w-4 text-red-600" />
            </NodeButton>
          )}
        </NodeToolbar>
      )}
      <div className={cn(`px-4 py-2 shadow-lg rounded-lg ${color} text-white relative group`, className)}>
        {!removeTargetHandle && (
          <Handle
            type="target"
            position={direction === "top-to-bottom" ? Position.Top : Position.Left}
            className="opacity-0! pointer-events-none"
            isConnectable={isTargetConnectable}
          />
        )}
        <div className="flex items-center gap-2">
          {icon && (
            <div className="w-6 h-6 flex items-center justify-center rounded-sm overflow-hidden bg-white/10 dark:text-white">
              {icon}
            </div>
          )}
          <div className="truncate">{data.label}</div>
        </div>
        <Handle
          type="source"
          position={direction === "top-to-bottom" ? Position.Bottom : Position.Right}
          isConnectable={isSourceConnectable}
          className="bg-gray-500! pointer-events-none opacity-0!"
        />
        {children}
      </div>

      {data.note && <div className="automation-node-note">{data.note}</div>}

      {showAddAction && !readOnly && (
        <NodeToolbar
          isVisible={isHovered}
          position={direction === "top-to-bottom" ? Position.Bottom : Position.Right}
          onMouseEnter={() => showToolbar()}
          onMouseLeave={() => hideToolbar()}
        >
          <NodeButton variant="ghost" size="icon" onClick={() => onAddAction?.({})}>
            <PlusIcon className="h-4 w-4" />
          </NodeButton>
        </NodeToolbar>
      )}
    </div>
  );
}

function NodeButton({ children, className, ...props }: ButtonProps) {
  return (
    <Button
      className={cn(
        "h-8 w-8 bg-white hover:bg-gray-100 shadow-sm shadow-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 dark:shadow-slate-200 dark:shadow-none dark:border dark:border-slate-500 dark:text-slate-200",
        className,
      )}
      {...props}
    >
      {children}
    </Button>
  );
}
